import './styles.css';
import { appHost } from './appHost';
import { AudioEngine } from './audioEngine';
import {
  ANCHOR_BODY,
  ANCHOR_CROWN,
  ANCHOR_FOREHEAD,
  ANCHOR_URNA,
  BUDDHA_DEFAULTS,
  buildBuddhaMask,
  fitBuddha,
  haloHeadroom,
  lightLevel,
  loadBuddhaConfig,
  saveBuddhaConfig,
  silhouetteExtent,
  sourceValue,
  type Box,
  type BuddhaConfig,
  type BuddhaMask,
  type LightLevels,
} from './buddha';
import { BuddhaFront } from './render/buddhaFront';
import { enterFullscreen, exitFullscreen, fullscreenSupported, isFullscreen, keepAwake, onFullscreenChange } from './fullscreen';
import { Library } from './library';
import { exportPresets, importPresets, loadUserPresets, saveUserPresets, uid } from './look';
import { Player, type RepeatMode } from './player';
import { GLRenderer, type BuddhaFrame } from './render/glRenderer';
import { LyricsLayer } from './render/lyrics';
import { Particles } from './render/particles';
import { findLineIndex, resolveLines, type ResolvedLine } from './timeline';
import type { Look, ScenePreset, Timeline } from './types';
import { $, download, fmtTime, ICON, pickFiles, toast } from './ui/dom';
import { Panel, type AppSettings } from './ui/panel';
import { PlaylistDrawer } from './ui/playlistDrawer';
import { TimelineEditor } from './ui/timelineEditor';

/* ------------------------------------------------------------------ */
/* 状态                                                                */
/* ------------------------------------------------------------------ */

const SETTINGS_KEY = 'nianfo.settings.v1';
const settings: AppSettings = { volume: 0.8, lyricOffset: 0, presetId: 'amita-red', repeat: 'all', ...loadSettings() };
function loadSettings(): Partial<AppSettings> {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? '{}') as Partial<AppSettings>;
  } catch {
    return {};
  }
}
function saveSettings(): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}
const DEFAULT_PRESET_MIGRATION_KEY = 'nianfo.default-preset.v1';
if (localStorage.getItem(DEFAULT_PRESET_MIGRATION_KEY) !== 'done') {
  if (settings.presetId === 'track') {
    settings.presetId = 'amita-red';
    saveSettings();
  }
  localStorage.setItem(DEFAULT_PRESET_MIGRATION_KEY, 'done');
}

const lib = new Library();
const player = new Player(lib);
const engine = new AudioEngine(player.audio);
if (appHost.enabled) {
  window.addEventListener('nianfo-host-lifecycle', () => { if (!appHost.active) player.pause(); });
  let lastSaved = 0;
  const savePosition = () => {
    if (player.track?.kind !== 'catalog' || !player.playlist || player.audio.readyState === 0 || player.audio.currentSrc !== player.track.src) return;
    localStorage.setItem('nianfo.app.playback.v1', JSON.stringify({ playlistId: player.playlist.id, trackId: player.track.id, time: player.audio.currentTime }));
  };
  player.audio.addEventListener('timeupdate', () => { if (performance.now() - lastSaved > 5000) { lastSaved = performance.now(); savePosition(); } });
  player.audio.addEventListener('pause', savePosition);
  player.audio.addEventListener('seeked', savePosition);
  window.addEventListener('pagehide', savePosition);
}
let userPresets: ScenePreset[] = loadUserPresets();
let look: Look = lib.lookFor(undefined, undefined);
/** 主尊佛像与五路光源：独立于场景预设，调整后立即保存在本机 */
let buddhaCfg: BuddhaConfig = loadBuddhaConfig();
let buddhaSaveTimer = 0;

let timeline: Timeline | null = null;
let timelineEdited = false;
let lines: ResolvedLine[] = [];
let lastLineIdx = -1;
let lyricPulse = 0;

/* ------------------------------------------------------------------ */
/* 渲染层                                                              */
/* ------------------------------------------------------------------ */

const stage = $('#stage');
const buddha = $<HTMLImageElement>('#buddha');
const glCanvas = $<HTMLCanvasElement>('#gl');
const gl = new GLRenderer(glCanvas);
if (!gl.ok) document.body.classList.add('no-webgl');
const particles = new Particles($<HTMLCanvasElement>('#fx'));
const front = new BuddhaFront($<HTMLCanvasElement>('#front'));
const lyrics = new LyricsLayer($('#lyrics'));

function resize(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  gl.resize(w, h, look.render.scale);
  particles.resize(w, h);
  front.resize(w, h);
}
window.addEventListener('resize', resize);

/* ------------------------------------------------------------------ */
/* 主尊布局：佛像居上、水平居中、完整呈现；底部为控制栏 / 首屏入口的独立区域  */
/* ------------------------------------------------------------------ */

let mask: BuddhaMask | null = null;
async function loadMask(): Promise<void> {
  try {
    await buddha.decode();
    mask = buildBuddhaMask(buddha);
    gl.setMask(mask);
    front.setMask(mask);
  } catch (e) {
    console.warn('佛像剪影遮罩生成失败', e);
  }
}

/** 读取 CSS 安全区（刘海屏） */
const safeProbe = document.createElement('div');
safeProbe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px)';
document.body.appendChild(safeProbe);
let safeTop = 0;
function readSafeTop(): void {
  safeTop = parseFloat(getComputedStyle(safeProbe).paddingTop) || 0;
}
window.addEventListener('resize', readSafeTop);
readSafeTop();

let curBox: Box | null = null;

/** 设计面板浮在画面上方，不改变舞台中心或佛像位置 */
function stageWidth(): number {
  return window.innerWidth;
}

/** 底部独立区域的高度：进入前为首屏入口，进入后为播放控制栏（窄屏打开面板时让出面板高度） */
function bottomReserve(): number {
  const vh = window.innerHeight;
  let r: number;
  if (!entered) {
    const dock = document.querySelector<HTMLElement>('.splash-dock');
    r = dock ? dock.getBoundingClientRect().height : 0;
  } else r = Math.min(controls.getBoundingClientRect().height, vh * 0.3);
  if (editor.open) r = Math.max(r, editor.el.getBoundingClientRect().height + 4);
  return r;
}

function targetBox(reserve: number): Box {
  const vw = stageWidth();
  const vh = window.innerHeight;
  return fitBuddha(vw, vh, {
    top: safeTop + Math.max(8, vh * 0.012),
    bottom: reserve,
    side: Math.max(8, vw * 0.02),
    size: buddhaCfg.size,
    headroom: haloHeadroom(buddhaCfg),
  });
}

/** 每帧：佛像框缓动到目标位置（进入念佛时较慢，拖动大小滑块时较快） */
function updateLayout(dt: number): Box {
  // 每帧只量一次底部区域高度（避免反复强制布局）
  const reserve = bottomReserve();
  const tgt = targetBox(reserve);
  if (!curBox) curBox = { ...tgt };
  const tau = layoutSlowUntil > performance.now() ? 0.35 : 0.08;
  const k = 1 - Math.exp(-dt / tau);
  for (const key of ['x', 'y', 'w', 'h'] as const) {
    const d = tgt[key] - curBox[key];
    curBox[key] = Math.abs(d) < 0.25 ? tgt[key] : curBox[key] + d * k;
  }
  const b = curBox;
  const css = [b.x, b.y, b.w, b.h].map((v) => v.toFixed(2) + 'px');
  const key = css.join(',');
  if (key !== appliedBox) {
    appliedBox = key;
    const st = buddha.style;
    [st.left, st.top, st.width, st.height] = css;
  }
  updateLyricEnv(b, reserve);
  return b;
}
let layoutSlowUntil = 0;
let appliedBox = '';

function headCenter(b: Box): { x: number; y: number; r: number } {
  return { x: b.x + ANCHOR_FOREHEAD.x * b.w, y: b.y + (ANCHOR_FOREHEAD.y + buddhaCfg.head.offsetY) * b.h, r: buddhaCfg.head.radius * b.h };
}

/** 歌词区：右上边缘竖排，避让佛像剪影与头部圆光，不进入底部控制区 */
function updateLyricEnv(b: Box, reserve: number): void {
  const vw = stageWidth();
  const vh = window.innerHeight;
  const top = safeTop + Math.max(16, vh * 0.045);
  const bottom = vh - reserve - Math.max(10, vh * 0.015);
  const hc = headCenter(b);
  const haloR = buddhaCfg.head.on ? hc.r * 1.06 : 0;
  const t = look.text;
  const sig = [vw, vh, b.x, b.y, b.w, haloR, top, bottom, t.size, t.spacing, t.layout, t.margin, !!mask].map((v) => (typeof v === 'number' ? Math.round(v) : String(v))).join(',');
  lyrics.setEnv(
    {
      vw,
      vh,
      top,
      bottom,
      margin: Math.min(72, Math.max(14, (t.margin / 100) * vw)),
      fPref: Math.min(vh * 0.085, (t.size / 100) * vh),
      fMin: 12,
      spacing: t.spacing,
      mode: t.layout,
      extent: (y0, y1) => {
        const e = silhouetteExtent(mask, b, y0, y1);
        if (haloR > 0) {
          const dy = hc.y < y0 ? y0 - hc.y : hc.y > y1 ? hc.y - y1 : 0;
          if (dy < haloR) {
            const half = Math.sqrt(haloR * haloR - dy * dy);
            e.left = Math.min(e.left, hc.x - half);
            e.right = Math.max(e.right, hc.x + half);
          }
        }
        return e;
      },
    },
    sig,
  );
}

/** 五路光源的本帧亮度（已含开关与音频驱动） */
function lightLevels(f: { drive: number; loudness: number; bands: [number, number, number, number]; active: boolean }, breath: number, pulse: number): LightLevels {
  const c = buddhaCfg;
  const fv = f.active ? f : { drive: breath, loudness: breath, bands: [breath, breath, breath, breath] as [number, number, number, number] };
  const lv = (on: boolean, base: number, react: number, src: Parameters<typeof sourceValue>[1], pg: number) => (on ? lightLevel(base, react, sourceValue(fv, src), pulse, pg) : 0);
  return {
    head: lv(c.head.on, c.head.intensity, c.head.react, c.head.source, 0.3),
    aura: lv(c.aura.on, c.aura.intensity, c.aura.react, c.aura.source, 0.15),
    contour: lv(c.contour.on, c.contour.intensity, c.contour.react, c.contour.source, 0.2),
    urna: lv(c.urna.on, c.urna.intensity, c.urna.react, c.urna.source, 0.35),
    crown: lv(c.crown.on, c.crown.intensity, c.crown.react, c.crown.source, 0.2),
    pulse,
  };
}

function buddhaFrames(b: Box, L: LightLevels): { back: BuddhaFrame; fore: Parameters<BuddhaFront['draw']>[0] } {
  const c = buddhaCfg;
  const hc = headCenter(b);
  const urna = { x: b.x + ANCHOR_URNA.x * b.w, y: b.y + ANCHOR_URNA.y * b.h };
  const crown = { x: b.x + ANCHOR_CROWN.x * b.w, y: b.y + ANCHOR_CROWN.y * b.h };
  return {
    back: {
      box: b,
      head: { ...hc, i: L.head, rings: c.head.rings, color: c.head.color, rayN: c.head.rayCount, rayI: c.head.rayIntensity, rayW: c.head.rayWidth, rayLen: c.head.rayLength, pulse: c.head.on ? Math.min(1, L.pulse * c.head.react) : 0 },
      aura: { x: b.x + ANCHOR_BODY.x * b.w, y: b.y + ANCHOR_BODY.y * b.h, i: L.aura, spread: c.aura.spread, flow: c.aura.flow, color: c.aura.color },
      rim: { i: L.contour, w: c.contour.width, color: c.contour.color },
      crown: { ...crown, r: 0.065 * b.h * c.crown.size, i: L.crown, beam: c.crown.beam, color: c.crown.color },
    },
    fore: {
      box: b,
      body: { i: c.aura.on ? c.aura.inner * (L.aura / Math.max(0.05, c.aura.intensity)) * 0.6 : 0, color: c.aura.color },
      rim: { i: c.contour.on ? c.contour.inner * L.contour * 0.6 : 0, color: c.contour.color },
      urna: { ...urna, r: Math.max(2, 0.0048 * b.h * c.urna.size), i: L.urna, color: c.urna.color, pulse: Math.min(1, L.pulse) },
      crown: { x: crown.x, y: crown.y - 0.012 * b.h, r: 0.03 * b.h * c.crown.size, i: L.crown, color: c.crown.color },
    },
  };
}

/* ------------------------------------------------------------------ */
/* 场景（Look）来源：跟随曲目 / 固定预设                                 */
/* ------------------------------------------------------------------ */

function allPresets(): ScenePreset[] {
  return [...lib.scenes.values(), ...userPresets];
}

function onBuddhaChange(): void {
  clearTimeout(buddhaSaveTimer);
  buddhaSaveTimer = window.setTimeout(() => saveBuddhaConfig(buddhaCfg), 250);
}

function applyLookFromSource(): void {
  const p = settings.presetId !== 'track' ? allPresets().find((x) => x.id === settings.presetId) : undefined;
  if (settings.presetId !== 'track' && !p) settings.presetId = 'track';
  // 尚未选曲（首屏）时使用第一个正式播单的场景，首屏与播放态保持同一视觉
  const pl = player.playlist ?? lib.playlists.find((x) => x.kind === 'catalog');
  look = p ? structuredClone(p.look) : lib.lookFor(player.track, pl);
  lyrics.applyLook(look.text);
  resize();
  panel.syncAll();
  updateNowPlaying();
}

/* ------------------------------------------------------------------ */
/* 时间轴                                                              */
/* ------------------------------------------------------------------ */

function setTimeline(tl: Timeline | null, edited: boolean): void {
  timeline = tl;
  timelineEdited = edited;
  lines = tl ? resolveLines(tl.lines) : [];
  lastLineIdx = -1;
  lyrics.clear();
  editor.refresh();
}

function timelineTime(): number {
  return player.audio.currentTime - settings.lyricOffset - (timeline?.offset ?? 0);
}

/* ------------------------------------------------------------------ */
/* UI 组件                                                             */
/* ------------------------------------------------------------------ */

const panel = new Panel({
  look: () => look,
  settings: () => settings,
  onLookChange: (path) => {
    if (path.startsWith('text.')) {
      lyrics.applyLook(look.text);
      if (path === 'text.animation' || path === 'text.duration') lyrics.replay();
    }
    if (path === 'render.scale') resize();
  },
  buddha: () => buddhaCfg,
  onBuddhaChange,
  resetBuddha: (group) => {
    if (group) (buddhaCfg as unknown as Record<string, unknown>)[group] = structuredClone(BUDDHA_DEFAULTS[group]);
    else buddhaCfg = structuredClone(BUDDHA_DEFAULTS);
    saveBuddhaConfig(buddhaCfg);
    panel.syncAll();
    toast(group ? '已恢复该光源的默认设置' : '已恢复佛像与光明的默认设置');
  },
  onSettingChange: (key) => {
    if (key === 'volume') applyVolume();
    saveSettings();
  },
  presets: allPresets,
  trackSceneName: () => lib.scenes.get(lib.sceneIdFor(player.track, player.playlist ?? lib.playlists.find((x) => x.kind === 'catalog')))?.name ?? '默认',
  selectPreset: (id) => {
    settings.presetId = id;
    saveSettings();
    applyLookFromSource();
  },
  savePresetAs: (name) => {
    const p: ScenePreset = { id: 'user-' + uid(), name: name || `${look.name} · 自定义`, look: structuredClone(look) };
    p.look.name = p.name;
    userPresets.push(p);
    saveUserPresets(userPresets);
    settings.presetId = p.id;
    saveSettings();
    applyLookFromSource();
    toast(`已保存预设「${p.name}」`);
  },
  overwritePreset: () => {
    const p = userPresets.find((x) => x.id === settings.presetId);
    if (!p) return toast('内置场景与「跟随曲目」不能覆盖，请用「另存为」');
    p.look = structuredClone(look);
    p.look.name = p.name;
    saveUserPresets(userPresets);
    toast(`已保存到「${p.name}」`);
  },
  deletePreset: () => {
    const p = userPresets.find((x) => x.id === settings.presetId);
    if (!p) return toast('只能删除自己保存的预设');
    if (!confirm(`删除预设「${p.name}」？`)) return;
    userPresets = userPresets.filter((x) => x !== p);
    saveUserPresets(userPresets);
    settings.presetId = 'track';
    saveSettings();
    applyLookFromSource();
  },
  importPresets: async () => {
    const [f] = await pickFiles('.json,application/json');
    if (!f) return;
    try {
      const list = importPresets(await f.text());
      userPresets.push(...list);
      saveUserPresets(userPresets);
      settings.presetId = list[0].id;
      saveSettings();
      applyLookFromSource();
      toast(`已导入 ${list.length} 个预设`);
    } catch (e) {
      toast('导入失败：' + (e as Error).message, 4000);
    }
  },
  exportCurrent: () => {
    const name = allPresets().find((x) => x.id === settings.presetId)?.name ?? look.name;
    download(`nianfo-preset-${name}.json`, exportPresets([{ id: settings.presetId, name, look }]));
  },
  exportAll: () => download('nianfo-presets.json', exportPresets(allPresets())),
  openTimelineEditor: () => openEditor(),
  timelineInfo: () => {
    if (!player.track) return '未选择曲目';
    if (!timeline) return '当前曲目没有时间轴（显示常驻文字）';
    const st = { aligned: '已对齐', draft: '草稿', test: '测试用' }[timeline.status ?? 'draft'];
    return `时间轴：${lines.length} 句 · ${st}${timelineEdited ? ' · 本地已编辑' : ''} · 本曲偏移 ${timeline.offset ?? 0}s`;
  },
  importAudio: () => void importAudio(),
});

const editor = new TimelineEditor({
  track: () => player.track,
  timeline: () => timeline,
  edited: () => timelineEdited,
  commit: (tl) => {
    const tr = player.track;
    if (!tr) return;
    lib.saveEdited(tr.id, tl);
    setTimeline(tl, true);
  },
  revert: async () => {
    const tr = player.track;
    if (!tr) return;
    lib.clearEdited(tr.id);
    setTimeline(await lib.originalTimeline(tr), false);
    toast('已恢复原始时间轴');
  },
  timelineTime,
  seekTimeline: (t) => player.seek(t + settings.lyricOffset + (timeline?.offset ?? 0)),
  togglePlay: () => player.toggle(),
});

const drawer = new PlaylistDrawer(lib, player, () => void importAudio());
document.body.append(drawer.el, panel.el, editor.el);

/** 窄屏上两个抽屉会互相遮挡，打开编辑器时先收起设计面板 */
function openEditor(): void {
  if (window.innerWidth <= 720) panel.toggle(false);
  editor.toggle(true);
}

async function importAudio(): Promise<void> {
  const files = await pickFiles('audio/*,.mp3,.m4a,.wav,.ogg,.flac,.json,.lrc', true);
  await addFiles(files);
}

async function addFiles(files: File[]): Promise<void> {
  if (!files.length) return;
  try {
    const ids = await lib.addLocalFiles(files);
    if (!ids.length) return toast('没有找到可播放的音频文件');
    const pl = lib.playlist('local-import')!;
    engine.init();
    await player.select(pl.id, pl.tracks.indexOf(ids[0]), true);
    toast(`已导入 ${ids.length} 个本地音频（仅本次会话）`);
  } catch (e) {
    toast('导入失败：' + (e as Error).message, 4000);
  }
}

/* ------------------------------------------------------------------ */
/* 播放器事件                                                          */
/* ------------------------------------------------------------------ */

const badge = $('#badge');

player.addEventListener('track', async () => {
  const tr = player.track;
  if (settings.presetId === 'track') applyLookFromSource();
  else {
    panel.refreshPresets();
    updateNowPlaying();
  }
  setTimeline(null, false);
  if (tr) {
    const id = tr.id;
    const res = await lib.timelineFor(tr);
    if (player.track?.id === id) setTimeline(res.timeline, res.edited);
  }
  // 测试音始终显示醒目标签，避免被当作真实曲目
  if (tr?.kind === 'test-tone') {
    badge.hidden = false;
    badge.textContent = '音频分析测试音 · 程序合成 · 非正式曲目';
    badge.className = 'badge test';
  } else if (tr?.kind === 'local') {
    badge.hidden = false;
    badge.textContent = '本地文件 · 仅本次会话';
    badge.className = 'badge local';
  } else badge.hidden = true;
});

player.addEventListener('state', () => {
  if (appHost.enabled && !appHost.active && player.playing) player.pause();
  updatePlayButton();
  void keepAwake(player.playing);
});
// 切回页面时重新申请屏幕常亮（系统在页面隐藏时会自动释放）
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void keepAwake(player.playing);
});
player.addEventListener('error', (e) => toast((e as CustomEvent<{ message: string }>).detail.message, 4000));

function updateNowPlaying(): void {
  const tr = player.track;
  $('#now-title').textContent = tr?.title ?? '在线念佛机';
  const sceneName = settings.presetId === 'track' ? look.name : `${look.name}（预设）`;
  $('#now-sub').textContent = [player.playlist?.title, sceneName].filter(Boolean).join(' · ');
  document.title = tr ? `${tr.title} · 在线念佛机` : '在线念佛机';
}

/* ------------------------------------------------------------------ */
/* 控制栏：点击画面显示，闲置后淡出                                      */
/* ------------------------------------------------------------------ */

const btn = {
  play: $<HTMLButtonElement>('#b-play'),
  prev: $<HTMLButtonElement>('#b-prev'),
  next: $<HTMLButtonElement>('#b-next'),
  list: $<HTMLButtonElement>('#b-list'),
  tune: $<HTMLButtonElement>('#b-tune'),
  full: $<HTMLButtonElement>('#b-full'),
  repeat: $<HTMLButtonElement>('#b-repeat'),
  vol: $<HTMLButtonElement>('#b-vol'),
};
btn.prev.innerHTML = ICON.prev;
btn.next.innerHTML = ICON.next;
btn.list.innerHTML = ICON.list;
btn.tune.innerHTML = ICON.tune;
const seek = $<HTMLInputElement>('#seek');
const volInput = $<HTMLInputElement>('#vol');
const controls = $('#controls');

let uiVisible = false;
let hideTimer = 0;
let pointerOverUi = false;

function showUI(autoHide = true): void {
  uiVisible = true;
  document.body.classList.add('ui-on');
  clearTimeout(hideTimer);
  if (autoHide) hideTimer = window.setTimeout(hideUI, 3500);
}
function hideUI(): void {
  if (pointerOverUi || seeking) {
    hideTimer = window.setTimeout(hideUI, 1500);
    return;
  }
  uiVisible = false;
  document.body.classList.remove('ui-on');
  $('.vol').classList.remove('open');
}

stage.addEventListener('click', () => {
  if (!entered) return;
  if (drawer.open) return drawer.toggle(false);
  if (uiVisible) hideUI();
  else showUI();
});
// 只有真实鼠标移动才唤出控制栏；触屏点击时浏览器补发的兼容鼠标事件不算
let lastMove = 0;
window.addEventListener('pointermove', (e) => {
  if (e.pointerType !== 'mouse') return;
  pointerOverUi = !!(e.target as HTMLElement).closest?.('.ui');
  if (!entered || e.timeStamp - lastMove < 200) return;
  lastMove = e.timeStamp;
  showUI();
});
window.addEventListener('pointerdown', (e) => {
  if (e.pointerType !== 'mouse') pointerOverUi = false;
}, true);
document.addEventListener('pointerleave', () => (pointerOverUi = false));
controls.addEventListener('pointerdown', () => showUI());

function updatePlayButton(): void {
  btn.play.innerHTML = player.playing ? ICON.pause : ICON.play;
  btn.play.setAttribute('aria-label', player.playing ? '暂停' : '播放');
  document.body.classList.toggle('is-playing', player.playing);
}
function updateRepeatButton(): void {
  const m = settings.repeat;
  btn.repeat.innerHTML = m === 'one' ? ICON.repeatOne : m === 'all' ? ICON.repeatAll : ICON.repeatNone;
  btn.repeat.title = { one: '单曲循环', all: '列表循环', none: '顺序播放（播完停止）' }[m];
}
function updateFullButton(): void {
  btn.full.innerHTML = (!appHost.connected && isFullscreen()) ? ICON.exitFull : ICON.full;
  btn.full.title = appHost.connected ? '切换APP全屏 (F)' : fullscreenSupported() ? (isFullscreen() ? '退出全屏 (F)' : '全屏 (F)') : '当前浏览器不支持系统全屏，已铺满视口；可「添加到主屏幕」获得全屏体验';
}
function applyVolume(): void {
  engine.setVolume(settings.volume);
  volInput.value = String(settings.volume);
  btn.vol.innerHTML = settings.volume === 0 ? ICON.mute : ICON.vol;
}

btn.play.addEventListener('click', () => {
  engine.init();
  if (!player.track) void startDefault(true);
  else player.toggle();
});
btn.prev.addEventListener('click', () => player.prev());
btn.next.addEventListener('click', () => player.next());
btn.list.addEventListener('click', () => drawer.toggle());
btn.tune.addEventListener('click', () => panel.toggle());
btn.full.addEventListener('click', () => void toggleFullscreen());
btn.repeat.addEventListener('click', () => {
  const order: RepeatMode[] = ['all', 'one', 'none'];
  settings.repeat = order[(order.indexOf(settings.repeat) + 1) % order.length];
  player.setRepeat(settings.repeat);
  updateRepeatButton();
  saveSettings();
  toast(btn.repeat.title, 1400);
});
btn.vol.addEventListener('click', () => $('.vol').classList.toggle('open'));
volInput.addEventListener('input', () => {
  settings.volume = Number(volInput.value);
  applyVolume();
  saveSettings();
});

let seeking = false;
seek.addEventListener('input', () => {
  seeking = true;
  const d = player.duration;
  $('#t-cur').textContent = fmtTime((Number(seek.value) / 1000) * d);
});
seek.addEventListener('change', () => {
  player.seek((Number(seek.value) / 1000) * player.duration);
  seeking = false;
});

async function toggleFullscreen(): Promise<void> {
  if (appHost.requestFullscreen()) return;
  if (isFullscreen()) await exitFullscreen();
  else if (!(await enterFullscreen())) toast('浏览器未允许系统全屏，已保持铺满视口');
}
onFullscreenChange(updateFullButton);

/* 键盘 */
window.addEventListener('keydown', (e) => {
  const tag = (e.target as HTMLElement).tagName;
  if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
  if (!entered) return;
  const k = e.key.toLowerCase();
  if (k === ' ') {
    e.preventDefault();
    engine.init();
    player.toggle();
  } else if (k === 'arrowright') player.seek(player.audio.currentTime + 5);
  else if (k === 'arrowleft') player.seek(player.audio.currentTime - 5);
  else if (k === 'n') player.next();
  else if (k === 'p') player.prev();
  else if (k === 'f') void toggleFullscreen();
  else if (k === 'd') panel.toggle();
  else if (k === 'l') drawer.toggle();
  else if (k === 't') {
    if (editor.open) editor.toggle(false);
    else openEditor();
  }
  else if (k === 'm' && editor.open) editor.mark();
  else if (k === 'escape') {
    if (editor.open) editor.toggle(false);
    else if (drawer.open) drawer.toggle(false);
    else if (panel.open) panel.toggle(false);
  } else return;
  showUI();
});

/* 拖放导入 */
window.addEventListener('dragover', (e) => e.preventDefault());
window.addEventListener('drop', (e) => {
  e.preventDefault();
  const files = [...(e.dataTransfer?.files ?? [])];
  if (files.length) void addFiles(files);
});

/* ------------------------------------------------------------------ */
/* 进入念佛                                                            */
/* ------------------------------------------------------------------ */

let entered = false;

/** 有正式曲目时自动播放第一个播单；否则只选中测试播单、不自动发声 */
async function startDefault(forcePlay = false): Promise<void> {
  if (appHost.enabled && player.track?.kind === 'catalog') { await player.play(); return; }
  const realPl = lib.playlists.find((p) => p.kind === 'catalog' && p.tracks.some((id) => lib.isPlayable(lib.tracks.get(id))));
  if (realPl) {
    await player.select(realPl.id, 0, true);
    return;
  }
  const testPl = lib.firstPlayable();
  if (testPl) await player.select(testPl.id, 0, forcePlay);
  if (!forcePlay) toast('尚未接入正式念佛音频。可按播放试听「音频分析测试音」调试光效，或在播单中导入本地音频。', 6000);
}

$('#enter').addEventListener('click', async () => {
  engine.init(); // 用户手势内创建 AudioContext
  if (!appHost.connected) void enterFullscreen().then((ok) => {
    if (!ok && fullscreenSupported()) toast('浏览器未允许系统全屏，已保持铺满视口，可随时点右下角全屏按钮');
    updateFullButton();
  });
  entered = true;
  layoutSlowUntil = performance.now() + 1600;
  document.body.classList.add('entered');
  setTimeout(() => $('#splash').remove(), 1600);
  await startDefault(false);
  showUI();
});

/* ------------------------------------------------------------------ */
/* 主循环：音频分析、歌词与光效共用同一播放时间                           */
/* ------------------------------------------------------------------ */

let lastT = performance.now();
/** 最近一帧的时钟与当前句（调试面板 / 本地实测用） */
let lastFrame = { audioTime: 0, tt: 0, idx: -1, text: '', fps: 0 };
let lastLevels: LightLevels = { head: 0, aura: 0, contour: 0, urna: 0, crown: 0, pulse: 0 };
let fps = 60;
let monitorAcc = 0;
const t0 = performance.now();

function frame(now: number): void {
  const dt = Math.min(0.1, (now - lastT) / 1000);
  lastT = now;
  fps += (1 / Math.max(dt, 0.001) - fps) * 0.05;

  const f = engine.update(dt, look.reactive);
  const r = look.reactive;

  // 歌词：以 audio.currentTime 为唯一时钟
  const hasAudio = !!player.audio.getAttribute('src');
  const tt = timelineTime();
  let idx = -1;
  if (lines.length && hasAudio) {
    idx = findLineIndex(lines, tt);
    if (idx !== lastLineIdx) {
      if (idx >= 0) lyricPulse = 1;
      lastLineIdx = idx;
    }
  }
  const box = updateLayout(dt);
  if (player.track?.id === 'FOW-U-165' && idx >= 0) {
    lyrics.show(`${player.track?.id}:${idx}`, lines[idx].text);
  } else if (player.track?.id === 'FOW-U-165' && (!lines.length || (!player.playing && player.audio.currentTime < 0.05))) {
    lyrics.show('ambient', player.track?.ambientText ?? '南无阿弥陀佛');
  } else {
    lyrics.clear();
  }
  editor.setActive(idx, player.playing);
  lastFrame = { audioTime: player.audio.currentTime, tt, idx, text: lyrics.text, fps };
  lyricPulse *= Math.exp(-dt / 0.6);

  // 驱动映射（未播放时保留缓慢呼吸）
  const breath = 0.12 * (0.5 + 0.5 * Math.sin((now - t0) / 1000 * 0.7));
  const drive = f.drive + (f.active ? 0 : breath);
  const pulse = Math.min(1.5, Math.max(f.onset * 0.8, lyricPulse * r.lyricPulse));
  const time = (now - t0) / 1000;

  const levels = lightLevels(f, breath * 2.2, Math.min(1, pulse));
  lastLevels = levels;
  const bf = buddhaFrames(box, levels);
  gl.render(look, { time, glow: drive * r.toGlow, rays: drive * r.toRays, rings: drive * r.toRings, pulse, low: f.bands[0] }, bf.back);
  front.draw(bf.fore);
  particles.update(look, dt, drive * r.toParticles, pulse);
  particles.draw(look, drive * r.toParticles, pulse);
  lyrics.setDrive(drive * r.toText + pulse * 0.3);

  // 进度
  if (!seeking) {
    const d = player.duration;
    const c = player.audio.currentTime;
    seek.value = String(d ? Math.round((c / d) * 1000) : 0);
    $('#t-cur').textContent = fmtTime(c);
    $('#t-dur').textContent = fmtTime(d);
  }

  monitorAcc += dt;
  if (monitorAcc > 0.066) {
    monitorAcc = 0;
    panel.updateMonitor(
      f,
      lastLevels,
      `音频 ${player.audio.currentTime.toFixed(2)}s · 时间轴 ${tt.toFixed(2)}s · 第 ${idx >= 0 ? idx + 1 : '—'} 句 · ${Math.round(fps)} fps · ${
        f.active ? '分析中' : '未播放'
      }`,
    );
  }
  requestAnimationFrame(frame);
}

/* ------------------------------------------------------------------ */
/* 启动                                                                */
/* ------------------------------------------------------------------ */

async function boot(): Promise<void> {
  updatePlayButton();
  updateRepeatButton();
  updateFullButton();
  applyVolume();
  player.setRepeat(settings.repeat);
  applyLookFromSource();
  void loadMask();
  requestAnimationFrame(frame);
  await lib.load(appHost.enabled ? '/catalog/catalog.json' : './catalog/catalog.json');
  if (appHost.enabled) {
    const raw = localStorage.getItem('nianfo.app.playback.v1');
    if (raw) {
      const saved = JSON.parse(raw) as { playlistId?: string; trackId?: string; time?: number };
      const pl = saved.playlistId ? lib.playlist(saved.playlistId) : undefined;
      const index = pl && saved.trackId ? pl.tracks.indexOf(saved.trackId) : -1;
      if (pl && index >= 0) {
        const restore = () => { if (player.track?.id === saved.trackId && typeof saved.time === 'number' && Number.isFinite(saved.time)) player.seek(saved.time); };
        player.audio.addEventListener('loadedmetadata', restore, { once: true });
        await player.select(pl.id, index, false);
      }
    }
  }
  if (lib.catalogError) toast(lib.catalogError, 5000);
  if (!player.track) applyLookFromSource();
  const realCount = [...lib.tracks.values()].filter((t) => t.kind === 'catalog' && t.src).length;
  $('#splash-note').textContent = realCount
    ? `已接入 ${realCount} 首念佛音频`
    : '正式念佛音频尚未接入 · 可用「音频分析测试音」或本地音频调试';
}

void boot();

// 开发模式下暴露只读调试句柄，供本地实测脚本核对「音频时间 ↔ 当前句」
if (import.meta.env.DEV) {
  (window as unknown as Record<string, unknown>).__nianfo = {
    player,
    lyrics,
    lines: () => lines,
    timelineTime,
    box: () => curBox,
    buddha: () => buddhaCfg,
    levels: () => lastLevels,
    frame: () => lastFrame,
    features: () => engine.features,
  };
}
